import { FC } from 'react';

import { Document, Page, Text, View, Image, Font } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import RobotoBold from '@/assets/fonts/Roboto-Bold.ttf';
import RobotoBoldItalic from '@/assets/fonts/Roboto-BoldItalic.ttf';
import RobotoItalic from '@/assets/fonts/Roboto-Italic.ttf';
import RobotoRegular from '@/assets/fonts/Roboto-Regular.ttf';
//import CoverPage from '@/assets/images/coverpage.png';
import ExportDataTr from '@/assets/images/export-data-tr.png';
import ExportData from '@/assets/images/export-data.png';
import IconEmail from '@/assets/images/icon-email.png';
import IconInternet from '@/assets/images/icon-internet.png';
import IconLocation from '@/assets/images/icon-location.png';
import OptimumSystem from '@/assets/images/opt-system.png';
import SectionsBg from '@/assets/images/reportBg.png';
import SimLogo from '@/assets/images/simlogo.png';
import ReportLogo from '@/assets/logo/logo.png';
import theme from '@/theme/index';
import { OrganizationCustomization } from '@/types/organization';
import { AnnualGenerationGraph } from '@/types/plant';
import { ReportData } from '@/types/report';
import { EnergyFlow } from '@/types/result';
import {
  SimulationBatteryDegradation,
  SimulationResult,
  SimulationResultMarketEnergyActivity,
  SimulationResultYearlyFinancialAnalysis,
} from '@/types/simulations';
import {
  formatCompactNumber,
  formatNumberWithCommas,
  formatDate,
  formatCompactInteger,
} from '@/utils';

import enTranslations from './en.json';
import { styles } from './styles';
import trTranslations from './tr.json';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatMetricName = (metric: string, t: any) => {
  return t.metricNames?.[metric] || metric;
};

const truncateName = (name: string, maxLength: number = 28) => {
  if (!name) return '';

  const cleanedName = name.trim().replace(/[-_\s]+$/, '');

  if (cleanedName.length <= maxLength) return cleanedName;
  return cleanedName.substring(0, maxLength).replace(/[-_\s]+$/, '') + '...';
};

const getSimulationNamesList = (
  simulations: SimulationResult[],
  andWord: string = 'and'
) => {
  if (!simulations || simulations.length === 0) return '';

  const names = simulations.map((sim) => truncateName(sim.name));

  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} ${andWord} ${names[1]}`;
  if (names.length > 2) {
    const lastTwo = names.slice(-2);
    const others = names.slice(0, -2);
    return `${others.join(', ')}, ${lastTwo[0]} ${andWord} ${lastTwo[1]}`;
  }

  return names.join(', ');
};

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: RobotoRegular,
    },
    {
      src: RobotoItalic,
      fontStyle: 'italic',
    },
    {
      src: RobotoBold,
      fontWeight: 700,
    },
    {
      src: RobotoBoldItalic,
      fontStyle: 'italic',
      fontWeight: 700,
    },
  ],
});

interface PDFDocumentProps {
  reportData: ReportData;
  bestSimulation: SimulationResult;
  dataFlowTotal: EnergyFlow;
  financialData: SimulationResultYearlyFinancialAnalysis;
  batteryDegData: SimulationBatteryDegradation;
  generatedGraphData: AnnualGenerationGraph;
  marketEnergyActivityData: SimulationResultMarketEnergyActivity;
  simulationNumber: number;
  selectedSimulations: SimulationResult[];
  language?: 'tr' | 'en';
  customization: OrganizationCustomization | null;
  currencySymbol: string;
}

interface FooterPDFProps {
  reportTitle: string;
  pageNumber: number;
  totalPages: number;
  logoData?: string;
  customization: OrganizationCustomization | null;
}

const Footer: FC<FooterPDFProps> = ({
  reportTitle,
  pageNumber,
  totalPages,
  logoData,
  customization,
}) => {
  const { t } = useTranslation();

  const logoSrc = customization?.logoLight || SimLogo;

  return (
    <View style={styles.footerStyle}>
      <Text style={{ color: theme.colors.light, fontSize: 10 }}>
        {t('common.page')} {pageNumber}/{totalPages}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          marginHorizontal: 20,
        }}
      >
        <Text style={{ color: theme.colors.light, fontSize: 10 }}>
          {reportTitle}{' '}
        </Text>
        {logoData && (
          <>
            <Text style={{ color: theme.colors.light, fontSize: 10 }}>- </Text>
            <Image
              src={logoData}
              style={{
                width: 65,
                height: 30,
                objectFit: 'contain',
              }}
            />
          </>
        )}
      </View>
      <Image
        src={logoSrc}
        style={{ width: 65, height: 30, objectFit: 'contain' }}
      />
    </View>
  );
};

const SimulationReportDocument: FC<PDFDocumentProps> = ({
  reportData,
  bestSimulation,
  dataFlowTotal,
  financialData,
  simulationNumber,
  batteryDegData,
  generatedGraphData,
  marketEnergyActivityData,
  selectedSimulations,
  language = 'tr',
  customization,
  currencySymbol,
}) => {
  const {
    reportName,
    reportTitle,
    selectedMetric,
    language: reportLanguage,
    chartImageData,
    energyFlowAnalysisData,
    metricsTableData,
    metricsData,
    batteryChargeData,
    batteryDischargeData,
    incomeBreakdownData,
    analysisTableData,
    ancillaryTableData,
    ebitdaStorageData,
    ebitdaWithStorageData,
    cashFlowStorageData,
    cashFlowWithStorageData,
    beforeStorageTableData,
    generationCardData,
    mcpChartData,
    pfcpChartData,
    sfcpChartData,
    energyActivityData,
    energyActivityAtMarketData,
    batteryLifeAndCycleData,
    infoCardData,
    pnlTableData,
    cashFlowTableData,
    parametersData,
    tableImages,
    logoData,
    plantData,
    storageData,
    scenarioData,
  } = reportData;

  const translations = {
    tr: trTranslations,
    en: enTranslations,
  };
  const currentLanguage = reportLanguage || language;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const t = translations[currentLanguage] as any;

  const getMarketCheckmarks = () => {
    if (!bestSimulation?.endDate || !scenarioData?.modelType) {
      return { dayAhead: '-', yekdem: '-', ancillary: '-', balancing: '-' };
    }

    const hasValidPlant = Boolean(
      plantData?.installedPowerKw &&
        plantData?.licensedPowerKw &&
        plantData?.generationType
    );

    // Gün Öncesi Piyasası kontrolü
    let dayAhead = '-';
    if (
      scenarioData.modelType === 'market_only' ||
      scenarioData.modelType === 'hybrid'
    ) {
      dayAhead = '+';
    } else if (
      scenarioData.modelType === 'feed_in_tariff_only' &&
      hasValidPlant &&
      plantData?.generationFeedInTariffEndDate &&
      dayjs(bestSimulation.endDate).isAfter(
        dayjs(plantData.generationFeedInTariffEndDate)
      )
    ) {
      dayAhead = '+';
    }

    // YEKDEM kontrolü
    let yekdem = '-';
    if (
      scenarioData.modelType === 'hybrid' ||
      scenarioData.modelType === 'feed_in_tariff_only'
    ) {
      yekdem = '+';
    }

    // Yan Hizmetler kontrolü
    let ancillary = '-';
    if (
      scenarioData.pfcParticipation === 2 ||
      scenarioData.sfcParticipation === 2
    ) {
      ancillary = '+';
    }

    // Dengeleme Güç Piyasası her zaman '-'
    const balancing = '-';

    return { dayAhead, yekdem, ancillary, balancing };
  };

  const marketCheckmarks = getMarketCheckmarks();

  const revenueModels = Object.entries(marketCheckmarks)
    .filter(([, value]) => value === '+')
    .map(([key]) => {
      switch (key) {
        case 'dayAhead':
          return t.markets.dayAheadMarket;
        case 'yekdem':
          return t.markets.yekdem;
        case 'ancillary':
          return t.markets.ancillaryServices;
        case 'balancing':
          return t.markets.balancingPowerMarket;
        default:
          return '';
      }
    })
    .join(', ');

  const simulationYear = dayjs(bestSimulation.endDate)
    .diff(dayjs(bestSimulation.startDate), 'year', true)
    .toFixed(0);

  const replacementYears = financialData?.graphData
    .filter((item) => item.batteryReplacementCount !== 0)
    .map((item) => item.year);

  // Plant verilerinin güvenli kontrolü için yardımcı fonksiyon
  const hasValidPlant = Boolean(
    plantData?.installedPowerKw &&
      plantData?.licensedPowerKw &&
      plantData?.generationType
  );

  let generationType = t.common.unknown;
  if (plantData?.generationType) {
    switch (plantData.generationType) {
      case 'wind':
        generationType = t.plantTypes.res;
        break;
      case 'solar':
        generationType = t.plantTypes.ges;
        break;
      case 'diesel':
        generationType = t.plantTypes.diesel;
        break;
      case 'other':
        generationType = t.plantTypes.hybrid;
        break;
      default:
        generationType = t.common.unknown;
    }
  }

  const isDependOnModelPfc = scenarioData.pfcParticipation === 2;
  const isDependOnModelSfc = scenarioData.sfcParticipation === 2;

  let baseTotalPages = 32;
  if (hasValidPlant) baseTotalPages += 4;
  if (isDependOnModelPfc) baseTotalPages += 2;
  if (isDependOnModelSfc) baseTotalPages += 1;

  const totalPages = baseTotalPages + (tableImages?.length || 0);
  let pageNumber = 1;

  const calculatePageNumbers = () => {
    let girdiAnaliziEkSayfa = 0;
    let temelGostergelerEkSayfa = 0;
    let batteryAndPlantEkSayfa = 0;
    if (hasValidPlant) {
      girdiAnaliziEkSayfa += 1;
      temelGostergelerEkSayfa += 1;
      batteryAndPlantEkSayfa += 2;
    }
    if (isDependOnModelPfc) girdiAnaliziEkSayfa += 1;
    if (isDependOnModelSfc) girdiAnaliziEkSayfa += 1;

    let fizibiliteBolumEkSayfa = 0;
    if (isDependOnModelPfc) fizibiliteBolumEkSayfa += 1;

    return {
      onsozStart: 3,
      girdiAnaliziStart: 6,
      temelGostergelerStart: 10 + girdiAnaliziEkSayfa,
      enerjiAnaliziStart: 13 + girdiAnaliziEkSayfa,
      finansalAnalizStart: 19 + girdiAnaliziEkSayfa + fizibiliteBolumEkSayfa,
      karsilastirmaliAnalizStart:
        27 +
        girdiAnaliziEkSayfa +
        fizibiliteBolumEkSayfa +
        batteryAndPlantEkSayfa,
      ekStart:
        28 +
        girdiAnaliziEkSayfa +
        fizibiliteBolumEkSayfa +
        batteryAndPlantEkSayfa +
        temelGostergelerEkSayfa +
        tableImages?.length,
    };
  };

  const pageNumbers = calculatePageNumbers();

  // const getUnitLabel = (value: string | number): string => {
  //   if (value === 'dollars') return '$';
  //   if (value === 'dollars_per_mwh') return '$/MWh';
  //   return '';
  // };

  const simulationPeriod =
    dayjs(bestSimulation.endDate).year() -
    dayjs(bestSimulation.startDate).year();

  const socDiff =
    (storageData?.maxAllowedStateOfCharge || 0) -
    (storageData?.minAllowedStateOfCharge || 0);
  const ratedPower = storageData?.ratedPowerMw || 0;
  const capacityValue = Number((ratedPower / (socDiff / 100)).toFixed(1));
  const roundTripEfficiency = storageData?.netOverallRoundTripEfficiency || 0;
  const preDropCapacity = Number(
    (capacityValue / (roundTripEfficiency / 100)).toFixed(1)
  );

  const fisibilityModel = hasValidPlant
    ? `${plantData?.installedPowerKw}MW/${plantData?.licensedPowerKw}MW ${generationType} & ${storageData?.ratedPowerMw}MW/${storageData?.ratedEnergyMwh}MWh BESS`
    : `${storageData?.ratedPowerMw}MW/${storageData?.ratedEnergyMwh}MWh BESS`;

  const logoSrc = customization?.logo || ReportLogo;

  return (
    <Document
      key={reportName}
      title={reportName}
      style={{ fontFamily: 'Roboto' }}
    >
      {/* Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image
            src={SectionsBg}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <View style={styles.absoluteCenter}>
            {reportTitle && (
              <Text
                style={{
                  fontSize: 32,
                  color: theme.colors.bluePrimary,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  width: '60%',
                  padding: 10,
                }}
              >
                {reportTitle}
              </Text>
            )}
            <Text
              style={{
                fontSize: 24,
                textAlign: 'center',
                color: theme.colors.primary,
                marginBottom: 10,
              }}
            >
              {reportName}
            </Text>
            <Image
              src={logoSrc}
              style={{
                width: 130,
                height: 60,
                objectFit: 'contain',
                marginTop: 30,
              }}
            />
            <View style={styles.absoluteBottom}>
              <View style={styles.infoRow}>
                <Image src={IconLocation} style={{ width: 14, height: 14 }} />
                <Text
                  style={{
                    fontSize: 14,
                    color: theme.colors.dark,
                  }}
                >
                  {customization?.address || 'ANKARA TEKMER UP'}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Image src={IconEmail} style={{ width: 14, height: 14 }} />
                <Text
                  style={{
                    fontSize: 14,
                    color: theme.colors.dark,
                  }}
                >
                  {customization?.email || 'contact@ratioenergy.co'}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Image src={IconInternet} style={{ width: 14, height: 14 }} />
                <Text
                  style={{
                    fontSize: 14,
                    color: theme.colors.dark,
                  }}
                >
                  {customization?.domain || 'www.ratiosim.com'}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
      {pageNumber++}
      {/* İçerik Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.tableOfContents.title}</Text>
        </View>
        <View
          style={[
            styles.section,
            {
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 10,
            },
          ]}
        >
          {/* Bölüm 1 Onsoz */}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>1</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.foreword}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.onsozStart}
              </Text>
            </View>
          </View>

          {/* Bölüm 2 Girdi Analizi*/}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>2</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.inputAnalysis}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.girdiAnaliziStart}
              </Text>
            </View>
          </View>

          {/* Bölüm 3.1 F.S - Temel Gostergeler*/}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>3.1</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.feasibilityResults} -{' '}
                {t.tableOfContents.sections.keyIndicators}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.temelGostergelerStart}
              </Text>
            </View>
          </View>

          {/* Bölüm 3.2 F.S - Enerji Analizi */}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>3.2</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.feasibilityResults} -{' '}
                {t.tableOfContents.sections.energyAnalysis}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.enerjiAnaliziStart}
              </Text>
            </View>
          </View>

          {/* Bölüm 3.3 - F.S - Finansal Analiz */}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>3.3</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.feasibilityResults} -{' '}
                {t.tableOfContents.sections.financialAnalysis}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.finansalAnalizStart}
              </Text>
            </View>
          </View>

          {/* Bölüm 4 */}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>4</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.comparativeAnalysis}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.karsilastirmaliAnalizStart}
              </Text>
            </View>
          </View>

          {/* Bölüm 5 */}
          <View style={styles.contentRow}>
            <View style={styles.contentNumberContainer}>
              <Text style={styles.contentNumber}>5</Text>
            </View>
            <View style={styles.contentTextContainer}>
              <Text style={styles.contentTitleText}>
                {t.tableOfContents.sections.appendices}
              </Text>
              <Text style={styles.contentPageNumber}>
                {t.common.page} {pageNumbers.ekStart}
              </Text>
            </View>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Önsöz Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.foreword.sectionNumber}
            </Text>
            <Text style={styles.coverPageText}>{t.foreword.title}</Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* Önsöz Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.foreword.title}</Text>
        </View>
        <View style={styles.section}>
          {/* Tablo Başlangıcı */}
          <View
            style={{
              borderWidth: 1,
              borderColor: theme.colors.grayLightAccent,
              backgroundColor: theme.colors.light,
              width: '100%',
              overflow: 'hidden',
              borderRadius: 6,
            }}
          >
            {/* Fizibilite Modeli Satırı */}
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
                minHeight: 40,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                    textAlign: 'center',
                  }}
                >
                  {t.foreword.feasibilityModel}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 4,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  {fisibilityModel}
                </Text>
              </View>
            </View>
            {/* Fizibilite Süresi Satırı */}
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
                minHeight: 40,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                    textAlign: 'center',
                  }}
                >
                  {t.foreword.feasibilityDuration}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 4,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {simulationYear}{' '}
                  {Number(simulationYear) > 1
                    ? t.foreword.years
                    : t.foreword.year}
                </Text>
              </View>
            </View>
            {/* Gelir Modelleri Başlık Satırı */}
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
                minHeight: 40,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2.22,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                    textAlign: 'center',
                  }}
                >
                  {t.foreword.revenueModels}
                </Text>
              </View>
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {t.foreword.dayAheadMarket}
                </Text>
              </View>
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {t.foreword.yekdem}
                </Text>
              </View>
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {t.foreword.ancillaryServices}
                </Text>
              </View>
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {t.foreword.balancing}
                </Text>
              </View>
            </View>
            {/* Checkmark Satırı */}
            <View
              style={{
                flexDirection: 'row',
                minHeight: 40,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2.22,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                    textAlign: 'center',
                  }}
                ></Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {marketCheckmarks.dayAhead}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {marketCheckmarks.yekdem}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {marketCheckmarks.ancillary}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                }}
              >
                <Text style={{ fontSize: 12, textAlign: 'center' }}>
                  {marketCheckmarks.balancing}
                </Text>
              </View>
            </View>
          </View>
          {/* Açıklama Metni */}
          <Text
            style={{
              fontSize: 12,
              marginBottom: 5,
              color: theme.colors.dark,
              marginTop: 20,
              lineHeight: 1.5,
            }}
          >
            {t.foreword.feasibilityAnalysisText
              .replace('{fisibilityModel}', fisibilityModel)
              .replace(/\\n/g, '\n')}
            {'\n\n'}
            {t.foreword.revenueModelsText
              .replace('{revenueModels}', revenueModels)
              .replace(/\\n/g, '\n')}
            {'\n\n'}
            {t.foreword.detailedInfoText.replace(/\\n/g, '\n')}
            {'\n\n'}
          </Text>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Önsöz - Simülasyon Sonuçları Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.foreword.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.foreword.bestResultsTitle}
          </Text>
        </View>
        <View style={styles.section}>
          <View style={[styles.boxContainer, { marginBottom: 10 }]}>
            <View style={styles.box}>
              <Text style={styles.boxTitle}>{t.foreword.simulationName}</Text>
              <Text style={{ fontSize: 12 }}>{bestSimulation.name}</Text>
            </View>
          </View>
          <View style={styles.boxContainer}>
            <View style={styles.box}>
              <Text style={styles.boxTitle}>{t.foreword.startDate}</Text>
              <Text style={{ fontSize: 12 }}>
                {formatDate(bestSimulation.startDate)}
              </Text>
            </View>

            <View style={styles.box}>
              <Text style={styles.boxTitle}>{t.foreword.endDate}</Text>
              <Text style={{ fontSize: 12 }}>
                {formatDate(bestSimulation.endDate)}
              </Text>
            </View>
          </View>
          <Text style={styles.text}>
            {'\n\n'}
            {t.foreword.analysisText
              .replace(/\\n/g, '\n')
              .split('{simulationNumber}')
              .map((part: string, index: number) => {
                if (index === 0)
                  return <Text key={`part-${index}`}>{part}</Text>;

                const restText = part
                  .replace(
                    '{selectedMetric}',
                    formatMetricName(selectedMetric, t)
                  )
                  .replace('{fisibilityModel}', fisibilityModel);

                // selectedMetric ve fisibilityModel için split
                const parts = restText.split(
                  new RegExp(
                    `(${formatMetricName(selectedMetric, t).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${fisibilityModel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                    'g'
                  )
                );

                return (
                  <Text key={`fragment-${index}`}>
                    <Text style={styles.blueText}>{simulationNumber}</Text>
                    {parts.map((textPart: string, partIndex: number) => {
                      if (textPart === formatMetricName(selectedMetric, t)) {
                        return (
                          <Text
                            key={`metric-${index}-${partIndex}`}
                            style={styles.blueText}
                          >
                            {textPart}
                          </Text>
                        );
                      } else if (textPart === fisibilityModel) {
                        return (
                          <Text
                            key={`model-${index}-${partIndex}`}
                            style={styles.blueText}
                          >
                            {textPart}
                          </Text>
                        );
                      } else {
                        return (
                          <Text key={`text-${index}-${partIndex}`}>
                            {textPart}
                          </Text>
                        );
                      }
                    })}
                  </Text>
                );
              })}
            {'\n\n'}
            {t.foreword.afterStoragePayback}{' '}
            <Text style={styles.blueText}>
              {bestSimulation.result.discountedPaybackPeriodProject}
            </Text>{' '}
            {bestSimulation.result.discountedPaybackPeriodProject > 1
              ? t.foreword.years
              : t.foreword.year}
            .{'\n\n'}
            {t.foreword.totalInvestmentCost}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatCompactNumber(bestSimulation.result.capexWithStorage)}
            </Text>
            , {t.foreword.annualOpex}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatCompactNumber(
                storageData?.fixedOpexValue +
                  (hasValidPlant ? plantData?.fixedOpexValue || 0 : 0)
              )}{' '}
              /{' '}
              {Number(
                storageData?.fixedOpexValue +
                  (hasValidPlant ? plantData?.fixedOpexValue || 0 : 0)
              ) > 1
                ? t.inputAnalysis.yrs
                : t.inputAnalysis.yr}
            </Text>
            .{'\n\n'}
            {t.foreword.projectEarnings
              .replace('{simulationPeriod}', simulationPeriod.toString())
              .replace(
                '{yearLabel}',
                simulationPeriod > 1 ? t.foreword.years : t.foreword.year
              )
              .replace(/\n/g, '\n')}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatCompactNumber(bestSimulation.result.ebitda)}
            </Text>{' '}
            {t.foreword.ebitdaLabel}.{'\n\n'}
            {t.foreword.totalGeneratedEnergy}{' '}
            <Text style={styles.blueText}>
              {formatNumberWithCommas(dataFlowTotal.graphData[0].generation)}{' '}
              MWh
            </Text>
            , {t.foreword.totalStoredEnergy}{' '}
            <Text style={styles.blueText}>
              {formatNumberWithCommas(dataFlowTotal.graphData[0].discharged)}{' '}
              MWh
            </Text>
            .
          </Text>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Girdi Analizi - Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.inputAnalysis.sectionNumber}
            </Text>
            <Text style={styles.coverPageText}>{t.inputAnalysis.title}</Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* Girdi Analizi - Finansal*/}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.inputAnalysis.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.inputAnalysis.financial}
          </Text>
        </View>
        <View style={[styles.section, { flexDirection: 'column', gap: 50 }]}>
          {/*CAPEX */}
          <View style={{ flexDirection: 'column', gap: 5 }}>
            <Text
              style={{ fontSize: 14, fontStyle: 'italic', fontWeight: 'bold' }}
            >
              {t.inputAnalysis.capex} :
            </Text>

            <View
              style={{
                border: `1px solid ${theme.colors.grayLightAccent}`,
                borderRadius: 6,
                flexDirection: 'column',
              }}
            >
              {/*Santral */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                  height: 20,
                  borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                }}
              >
                <Text
                  style={{
                    flex: 1,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    fontSize: 12,
                    fontWeight: 'bold',
                  }}
                >
                  {t.inputAnalysis.plant}
                </Text>
                <Text
                  style={{
                    flex: 4,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    fontSize: 12,
                  }}
                >
                  {t.inputAnalysis.totalInvestment} :{' '}
                  {hasValidPlant
                    ? formatCompactNumber(Number(plantData?.capexValue))
                    : '-'}
                </Text>
                <Text
                  style={{
                    flex: 4,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    fontSize: 12,
                  }}
                >
                  {t.inputAnalysis.debtToCapital} : %
                  {hasValidPlant
                    ? formatCompactInteger(
                        Number(plantData?.loanFundedCapexRatio)
                      )
                    : ' -'}
                </Text>
                <Text
                  style={{
                    flex: 4,
                    fontSize: 12,
                  }}
                >
                  {t.inputAnalysis.loanRepaymentPeriod} :{' '}
                  {hasValidPlant ? plantData?.loanRepaymentPeriod : '-'}{' '}
                  {Number(plantData?.loanRepaymentPeriod || 0) > 1
                    ? t.inputAnalysis.years
                    : t.inputAnalysis.year}
                </Text>
              </View>
              {/*BESS */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                  height: 40,
                }}
              >
                <View
                  style={{
                    flex: 1,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Text style={styles.textBold}>{t.inputAnalysis.bess}</Text>
                </View>
                <View
                  style={{
                    flex: 12,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 40,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: 20,
                      borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                    }}
                  >
                    <Text
                      style={{
                        flex: 0.8,
                        fontSize: 12,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      }}
                    >
                      {t.inputAnalysis.debtToCapital} :{' '}
                      {`%${formatCompactInteger(Number(storageData.loanFundedCapexRatioBattery))}`}
                    </Text>
                    <Text
                      style={{
                        flex: 0.8,
                        fontSize: 12,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      }}
                    >
                      {t.inputAnalysis.debtToCapitalBos} :{' '}
                      {`%${formatCompactInteger(Number(storageData.loanFundedCapexRatioBos))}`}
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.loanRepaymentPeriodBattery} :{' '}
                      {`${storageData.loanRepaymentPeriodBattery} ${
                        Number(storageData.loanRepaymentPeriodBattery || 0) > 1
                          ? t.inputAnalysis.years
                          : t.inputAnalysis.year
                      }`}
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: 20,
                    }}
                  >
                    <Text
                      style={{
                        flex: 0.8,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.batteries} :{' '}
                      {`${currencySymbol}${formatCompactNumber(Number(storageData.batteryCapexValue))}/MWh`}
                    </Text>
                    <Text
                      style={{
                        flex: 0.8,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.pcsAndBos} :{' '}
                      {`${currencySymbol}${formatCompactNumber(Number(storageData.pcsCostValue || 0))}/MW & ${formatCompactNumber(storageData.bosCostValue || 0)}/${currencySymbol}`}
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.loanRepaymentPeriodBos} :{' '}
                      {`${storageData.loanRepaymentPeriodBos} ${
                        Number(storageData.loanRepaymentPeriodBos || 0) > 1
                          ? t.inputAnalysis.years
                          : t.inputAnalysis.year
                      }`}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
          {/*Sabit OPEX  &  Degistirilen Plant OPEX & Degisken SS OPEX*/}
          <View
            style={{
              flexDirection: 'row',
              gap: 10,
            }}
          >
            {/*Sabit OPEX */}
            <View style={{ flexDirection: 'column', gap: 5, flex: 1.25 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                }}
              >
                {t.inputAnalysis.fixedOpex} :
              </Text>

              <View
                style={{
                  border: `1px solid ${theme.colors.grayLightAccent}`,
                  borderRadius: 6,
                  flexDirection: 'column',
                }}
              >
                {/*Santral */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    height: 20,
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <Text
                    style={{
                      flex: 1,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {t.inputAnalysis.plant}
                  </Text>
                  <Text
                    style={{
                      flex: 2.5,
                      fontSize: 12,
                    }}
                  >
                    {hasValidPlant
                      ? `${currencySymbol}${formatCompactNumber(Number(plantData?.fixedOpexValue))}`
                      : `${currencySymbol}-`}{' '}
                    {/* TODO : unit Ekle */}/{' '}
                    {Number(plantData?.fixedOpexValue || 0) > 1
                      ? t.inputAnalysis.yrs
                      : t.inputAnalysis.yr}
                  </Text>
                </View>
                {/*BESS */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    height: 20,
                  }}
                >
                  <Text
                    style={{
                      flex: 1,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {t.inputAnalysis.bess}
                  </Text>
                  <Text
                    style={{
                      flex: 2.5,
                      fontSize: 12,
                    }}
                  >
                    {`${currencySymbol}${formatCompactNumber(Number(storageData.fixedOpexValue))}`}{' '}
                    /{' '}
                    {Number(storageData.fixedOpexValue || 0) > 1
                      ? t.inputAnalysis.yrs
                      : t.inputAnalysis.yr}
                    {/* TODO : unit Ekle */}
                  </Text>
                </View>
              </View>
            </View>
            {/*Degisken Plant OPEX */}
            <View style={{ flexDirection: 'column', gap: 5, flex: 1.95 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                }}
              >
                {t.inputAnalysis.variablePlantOpex} :
              </Text>

              <View
                style={{
                  border: `1px solid ${theme.colors.grayLightAccent}`,
                  borderRadius: 6,
                  flexDirection: 'column',
                }}
              >
                {/*Satılan Enerjinin Dağıtım Ücreti */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    height: 20,
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <Text
                    style={{
                      flex: 3,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {t.inputAnalysis.distributionFeeSold}
                  </Text>
                  <Text
                    style={{
                      flex: 1.2,
                      fontSize: 12,
                    }}
                  >
                    {hasValidPlant &&
                    Number.isFinite(Number(plantData?.distributionFeePerMwh))
                      ? `${currencySymbol}${formatCompactNumber(Number(plantData?.distributionFeePerMwh))}`
                      : `${currencySymbol}-`}{' '}
                    / MWh
                  </Text>
                </View>
                {/*Satın Alınan Enerjinin Dağıtım Ücreti  */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    height: 20,
                  }}
                >
                  <Text
                    style={{
                      flex: 3,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {t.inputAnalysis.distributionFeePurchased}
                  </Text>
                  <Text
                    style={{
                      flex: 1.2,
                      fontSize: 12,
                    }}
                  >
                    {hasValidPlant &&
                    Number.isFinite(Number(plantData?.withdrawalFeePerMwh))
                      ? `${currencySymbol}${formatCompactNumber(Number(plantData?.withdrawalFeePerMwh))}`
                      : `${currencySymbol}-`}{' '}
                    / MWh
                  </Text>
                </View>
              </View>
            </View>
            {/*Degisken SS OPEX */}
            <View style={{ flexDirection: 'column', gap: 5, flex: 1.95 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                }}
              >
                {t.inputAnalysis.variableStorageOpex} :
              </Text>

              <View
                style={{
                  border: `1px solid ${theme.colors.grayLightAccent}`,
                  borderRadius: 6,
                  flexDirection: 'column',
                }}
              >
                {/*Satılan Enerjinin Dağıtım Ücreti */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    height: 20,
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <Text
                    style={{
                      flex: 3,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {t.inputAnalysis.distributionFeeSold}
                  </Text>
                  <Text
                    style={{
                      flex: 1.2,
                      fontSize: 12,
                    }}
                  >
                    {Number.isFinite(Number(storageData?.distributionFeePerMwh))
                      ? `${currencySymbol}${formatCompactNumber(Number(storageData.distributionFeePerMwh))}`
                      : `${currencySymbol}-`}{' '}
                    / MWh
                  </Text>
                </View>
                {/*BESS */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    height: 20,
                  }}
                >
                  <Text
                    style={{
                      flex: 3,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    {t.inputAnalysis.distributionFeePurchased}
                  </Text>
                  <Text
                    style={{
                      flex: 1.2,
                      fontSize: 12,
                    }}
                  >
                    {Number.isFinite(Number(storageData?.withdrawalFeePerMwh))
                      ? `${currencySymbol}${formatCompactNumber(Number(storageData.withdrawalFeePerMwh))}`
                      : `${currencySymbol}-`}{' '}
                    / MWh
                  </Text>
                </View>
              </View>
            </View>
          </View>
          {/*Temel Ekonomik Parametreler */}
          <View style={{ flexDirection: 'column', gap: 5 }}>
            <Text
              style={{ fontSize: 14, fontStyle: 'italic', fontWeight: 'bold' }}
            >
              {t.inputAnalysis.economicParameters} :
            </Text>

            <View
              style={{
                border: `1px solid ${theme.colors.grayLightAccent}`,
                borderRadius: 6,
                flexDirection: 'column',
              }}
            >
              {/*Santral */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                  height: 20,
                  borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                }}
              >
                <Text
                  style={{
                    flex: 1,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    fontSize: 12,
                    fontWeight: 'bold',
                  }}
                >
                  {t.inputAnalysis.plant}
                </Text>
                <Text
                  style={{
                    flex: 4,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    fontSize: 12,
                  }}
                >
                  {t.inputAnalysis.wacc} :{' '}
                  {`%${formatCompactNumber(Number(scenarioData.hurdleRate))}`}
                </Text>
                <Text
                  style={{
                    flex: 4,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    fontSize: 12,
                  }}
                >
                  {t.inputAnalysis.incomeTax} :{' '}
                  {`%${formatCompactNumber(Number(scenarioData.taxRate))}`}
                </Text>
                <Text
                  style={{
                    flex: 4,
                    fontSize: 12,
                  }}
                >
                  {t.inputAnalysis.depreciation} :{' '}
                  {hasValidPlant ? plantData?.depreciationYears : '-'}{' '}
                  {Number(plantData?.depreciationYears || 0) > 1
                    ? t.inputAnalysis.yrs
                    : t.inputAnalysis.yr}
                </Text>
              </View>
              {/*BESS */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                  height: 40,
                }}
              >
                <View
                  style={{
                    flex: 1,
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Text style={styles.textBold}>{t.inputAnalysis.bess}</Text>
                </View>
                <View
                  style={{
                    flex: 12,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 40,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: 20,
                      borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                    }}
                  >
                    <Text
                      style={{
                        flex: 1,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.wacc} :{' '}
                      {`%${formatCompactNumber(Number(scenarioData.hurdleRate))}`}
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 12,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      }}
                    >
                      {t.inputAnalysis.incomeTax} :{' '}
                      {`%${formatCompactNumber(Number(scenarioData.taxRate))}`}
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.annualPriceDecline} :{' '}
                      {`%${formatCompactNumber(Number(scenarioData.batteryPriceDeclinePercentagePerYear))}`}{' '}
                      / {t.inputAnalysis.yr}
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: 20,
                    }}
                  >
                    <Text
                      style={{
                        flex: 1,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.depreciationBattery} :{' '}
                      {`${storageData.depreciationYearsBattery} ${
                        Number(storageData.depreciationYearsBattery || 0) > 1
                          ? t.inputAnalysis.yrs
                          : t.inputAnalysis.yr
                      }`}
                    </Text>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 12,
                      }}
                    >
                      {t.inputAnalysis.depreciationBos} :{' '}
                      {`${storageData.depreciationYearsBos} ${
                        Number(storageData.depreciationYearsBos || 0) > 1
                          ? t.inputAnalysis.yrs
                          : t.inputAnalysis.yr
                      }`}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Girdi Analizi - Depolama Sistemi*/}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.inputAnalysis.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.inputAnalysis.storageSystem}
          </Text>
        </View>
        <View style={styles.section}>
          <View
            style={{
              border: `1px solid ${theme.colors.grayLightAccent}`,
              borderBottom: 'none',
              borderRadius: 6,
              flexDirection: 'column',
              fontSize: 14,
              marginBottom: 10,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 2.5,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.ratedPower}</Text>
              </View>

              <View
                style={{
                  flex: 1,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{storageData.ratedPowerMw}</Text>
              </View>
              <View
                style={{
                  flex: 2.5,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.ratedEnergy}</Text>
              </View>
              <View
                style={{
                  flex: 1,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{storageData.ratedEnergyMwh}</Text>
              </View>
              <View
                style={{
                  flex: 2.5,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.minSoh}</Text>
              </View>
              <View
                style={{
                  flex: 1,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>%{storageData.minAllowedSoh}</Text>
              </View>
              <View
                style={{
                  flex: 2.5,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.roundTripEff}</Text>
              </View>
              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  %{storageData.netOverallRoundTripEfficiency}
                </Text>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 2.5,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.minSoc}</Text>
              </View>
              <View
                style={{
                  flex: 1,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  %{storageData.minAllowedStateOfCharge}
                </Text>
              </View>
              <View
                style={{
                  flex: 2.5,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.maxSoc}</Text>
              </View>
              <View
                style={{
                  flex: 1,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  %{storageData.maxAllowedStateOfCharge}
                </Text>
              </View>
              <View
                style={{
                  flex: 2.5,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                }}
              >
                <Text style={styles.text}>
                  {t.inputAnalysis.gridPowerConstraint.replace(/\\n/g, '\n')}
                </Text>
              </View>
              <View
                style={{
                  flex: 1,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                }}
              >
                <Text style={styles.text}>
                  {storageData.gridPowerConstraintInflow}
                </Text>
              </View>
              <View
                style={{
                  flex: 2.5,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                }}
              >
                <Text style={styles.text}>
                  {t.inputAnalysis.gridPowerOutputConstraint.replace(
                    /\\n/g,
                    '\n'
                  )}
                </Text>
              </View>
              <View
                style={{
                  flex: 1,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {storageData.gridPowerConstraintOutflow}
                </Text>
              </View>
            </View>

            {storageData?.rateCapabilities?.length > 0 ? (
              (() => {
                const capabilities = storageData.rateCapabilities;
                const length = capabilities.length;

                // Sütun sayısını belirle
                let columns = 1;
                if (length > 6) columns = 3;
                else if (length > 3) columns = 2;

                // Array'i chunks'lara böl (her chunk bir satır)
                const rows = [];
                for (
                  let i = 0;
                  i < Math.min(3, Math.ceil(length / columns));
                  i++
                ) {
                  const rowItems = [];
                  for (let j = 0; j < columns; j++) {
                    const index = i * columns + j;
                    if (index < length) {
                      rowItems.push(capabilities[index]);
                    }
                  }
                  rows.push(rowItems);
                }

                return rows.map((rowItems, rowIndex) => (
                  <View
                    key={rowIndex}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 40,
                    }}
                  >
                    {rowItems.map((capability, colIndex) => (
                      <View
                        key={colIndex}
                        style={{ flex: 1, flexDirection: 'row', height: 40 }}
                      >
                        <View
                          style={{
                            flex: 4.5,
                            height: 40,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: theme.colors.blueLightPale,
                            borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                          }}
                        >
                          <Text style={styles.text}>
                            {t.inputAnalysis.fullEquivalentCycle} at{' '}
                            {capability.cRate}C
                          </Text>
                        </View>
                        <View
                          style={{
                            flex: 1.5,
                            height: 40,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRight:
                              colIndex === rowItems.length - 1
                                ? 'none'
                                : `1px solid ${theme.colors.grayLightAccent}`,
                          }}
                        >
                          <Text style={styles.text}>
                            {capability.cycleLife || '-'}
                          </Text>
                        </View>
                      </View>
                    ))}
                    {Array.from({ length: columns - rowItems.length }).map(
                      (_, emptyIndex) => (
                        <View
                          key={`empty-${emptyIndex}`}
                          style={{ flex: 1, flexDirection: 'row', height: 40 }}
                        >
                          <View
                            style={{
                              flex: 2,
                              height: 40,
                              borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                            }}
                          />
                          <View
                            style={{
                              flex: 1,
                              height: 40,
                              borderRight:
                                emptyIndex === columns - rowItems.length - 1
                                  ? 'none'
                                  : `1px solid ${theme.colors.grayLightAccent}`,
                            }}
                          />
                        </View>
                      )
                    )}
                  </View>
                ));
              })()
            ) : (
              <View
                style={{
                  flex: 6,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>-</Text>
              </View>
            )}
          </View>
        </View>

        <Text style={[styles.text, { marginLeft: 10 }]}>
          1- {t.inputAnalysis.batteryNote1.replace(/\\n/g, '\n')} {'\n\n'}2-{' '}
          {t.inputAnalysis.batteryNote2.replace(/\\n/g, '\n')}
          {'\n\n'}
          3- {t.inputAnalysis.batteryNote3.replace(/\\n/g, '\n')}
          {'\n\n'}
        </Text>

        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Girdi Analizi - XXX MWm / XXX MWe RES*/}
      {hasValidPlant && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>{t.inputAnalysis.title} </Text>
              <Text style={styles.headerSubtitle}>
                - {plantData.installedPowerKw}MWm / {plantData.licensedPowerKw}
                MWe {generationType}
              </Text>
            </View>
            <View style={styles.section}>
              <View
                style={{
                  border: `1px solid ${theme.colors.grayLightAccent}`,
                  borderRadius: 6,
                  flexDirection: 'row',
                  fontSize: 14,
                  marginBottom: 20,
                }}
              >
                <View
                  style={{
                    flex: 1,
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <Text style={styles.textBoldTitle}>{generationType}</Text>
                </View>
                <View
                  style={{
                    flex: 10.5,
                    flexDirection: 'column',
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                    }}
                  >
                    <View
                      style={{
                        flex: 2.5,
                        backgroundColor: theme.colors.blueLightPale,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {t.inputAnalysis.installedPower}
                      </Text>
                    </View>

                    <View
                      style={{
                        flex: 1,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {plantData.installedPowerKw}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 2.5,
                        backgroundColor: theme.colors.blueLightPale,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {t.inputAnalysis.licensedPower}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 1,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {plantData.licensedPowerKw}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 2.5,
                        backgroundColor: theme.colors.blueLightPale,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {t.inputAnalysis.operationalLifespan}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 1,
                        height: 25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>{plantData.lifespan}</Text>
                    </View>
                  </View>

                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <View
                      style={{
                        flex: 2.5,
                        backgroundColor: theme.colors.blueLightPale,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {t.inputAnalysis.capacityFactorMechanical.replace(
                          /\\n/g,
                          '\n'
                        )}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 1,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {' '}
                        %{' '}
                        {Number(
                          generatedGraphData?.capacityFactorMechanic
                        ).toFixed(2)}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 2.5,
                        backgroundColor: theme.colors.blueLightPale,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {t.inputAnalysis.capacityFactorElectric.replace(
                          /\\n/g,
                          '\n'
                        )}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 1,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {' '}
                        %{' '}
                        {Number(
                          generatedGraphData?.capacityFactorElectric
                        ).toFixed(2)}
                      </Text>
                    </View>

                    <View
                      style={{
                        flex: 2.5,
                        backgroundColor: theme.colors.blueLightPale,
                        borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        {t.inputAnalysis.curtailmentPercentage}
                      </Text>
                    </View>

                    <View
                      style={{
                        flex: 1,
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={styles.text}>
                        %{' '}
                        {Number(
                          generatedGraphData?.curtailmentPercentage
                        ).toFixed(2)}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginTop: 5,
                }}
              >
                {generationCardData && (
                  <Image
                    src={generationCardData}
                    style={{
                      width: '100%',
                      height: 230,
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
              <View
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 5,
                  marginTop: 5,
                }}
              >
                <Text style={styles.textItalic}>
                  {t.inputAnalysis.chartBelongsToYear.replace(
                    '{year}',
                    hasValidPlant
                      ? dayjs(plantData.startDate).format('YYYY')
                      : dayjs(bestSimulation.startDate).format('YYYY')
                  )}
                </Text>
              </View>
            </View>

            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}

      {/* Girdi Analizi -{scenarioData.name} & PTF Analizi -mcp*/}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.inputAnalysis.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {truncateName(scenarioData.name)} & {t.inputAnalysis.mcpAnalysis}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.inputAnalysis.mcpDistributionText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              border: `1px solid ${theme.colors.grayLightAccent}`,
              borderRadius: 6,
              flexDirection: 'column',
              fontSize: 14,
              marginBottom: 5,
              marginTop: 5,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.inputAnalysis.mcpStatistics}</Text>
              </View>

              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {String(t.inputAnalysis.average).replace(
                    '{symbol}',
                    currencySymbol
                  )}
                </Text>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {dayjs(bestSimulation.startDate).format('YYYY')}
                </Text>
              </View>

              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {formatCompactNumber(
                    marketEnergyActivityData.graphData[0].mcp
                  )}
                </Text>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {dayjs(bestSimulation.startDate).format('YYYY')} -{' '}
                  {dayjs(bestSimulation.endDate).format('YYYY')}
                </Text>
              </View>

              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {formatCompactNumber(
                    marketEnergyActivityData.graphData.reduce(
                      (acc, curr) => acc + curr.mcp,
                      0
                    ) / marketEnergyActivityData.graphData.length
                  )}
                </Text>
              </View>
            </View>
          </View>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 5,
            }}
          >
            {mcpChartData && (
              <Image
                src={mcpChartData}
                style={{
                  width: '100%',
                  height: 225,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Girdi Analizi -{scenarioData.name} & PFK Fiyat Analizi -pfcp*/}
      {isDependOnModelPfc && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>{t.inputAnalysis.title} </Text>
              <Text style={styles.headerSubtitle}>
                - {truncateName(scenarioData.name)} &{' '}
                {t.inputAnalysis.pfcPriceAnalysis}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.text}>
                {t.inputAnalysis.pfcDistributionText.replace(/\\n/g, '\n')}
              </Text>
              <View
                style={{
                  border: `1px solid ${theme.colors.grayLightAccent}`,
                  borderRadius: 6,
                  flexDirection: 'column',
                  fontSize: 14,
                  marginBottom: 5,
                  marginTop: 5,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {t.inputAnalysis.pfcStatistics}
                    </Text>
                  </View>

                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {String(t.inputAnalysis.average).replace(
                        '{symbol}',
                        currencySymbol
                      )}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {dayjs(bestSimulation.startDate).format('YYYY')}
                    </Text>
                  </View>

                  <View
                    style={{
                      flex: 1,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {formatCompactNumber(
                        marketEnergyActivityData.graphData[0].pfcPrice
                      )}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {dayjs(bestSimulation.startDate).format('YYYY')} -{' '}
                      {dayjs(bestSimulation.endDate).format('YYYY')}
                    </Text>
                  </View>

                  <View
                    style={{
                      flex: 1,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {formatCompactNumber(
                        marketEnergyActivityData.graphData.reduce(
                          (acc, curr) => acc + curr.pfcPrice,
                          0
                        ) / marketEnergyActivityData.graphData.length
                      )}
                    </Text>
                  </View>
                </View>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginTop: 5,
                }}
              >
                {pfcpChartData && (
                  <Image
                    src={pfcpChartData}
                    style={{
                      width: '100%',
                      height: 225,
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
            </View>

            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}
      {/* Girdi Analizi -{scenarioData.name} & SFK Fiyat Analizi -sfcp*/}
      {isDependOnModelSfc && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>{t.inputAnalysis.title} </Text>
              <Text style={styles.headerSubtitle}>
                - {truncateName(scenarioData.name)} &{' '}
                {t.inputAnalysis.sfcPriceAnalysis}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.text}>
                {t.inputAnalysis.sfcDistributionText.replace(/\\n/g, '\n')}
              </Text>
              <View
                style={{
                  border: `1px solid ${theme.colors.grayLightAccent}`,
                  borderRadius: 6,
                  flexDirection: 'column',
                  fontSize: 14,
                  marginBottom: 5,
                  marginTop: 5,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {t.inputAnalysis.sfcStatistics}
                    </Text>
                  </View>

                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {String(t.inputAnalysis.average).replace(
                        '{symbol}',
                        currencySymbol
                      )}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {dayjs(bestSimulation.startDate).format('YYYY')}
                    </Text>
                  </View>

                  <View
                    style={{
                      flex: 1,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {formatCompactNumber(
                        marketEnergyActivityData.graphData[0].sfcPrice
                      )}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.blueLightPale,
                      borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {dayjs(bestSimulation.startDate).format('YYYY')} -{' '}
                      {dayjs(bestSimulation.endDate).format('YYYY')}
                    </Text>
                  </View>

                  <View
                    style={{
                      flex: 1,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={styles.text}>
                      {formatCompactNumber(
                        marketEnergyActivityData.graphData.reduce(
                          (acc, curr) => acc + curr.sfcPrice,
                          0
                        ) / marketEnergyActivityData.graphData.length
                      )}
                    </Text>
                  </View>
                </View>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginTop: 5,
                }}
              >
                {sfcpChartData && (
                  <Image
                    src={sfcpChartData}
                    style={{
                      width: '100%',
                      height: 225,
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
            </View>

            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}
      {/* F.S*1 - Temel Gostergeler - Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.feasibilityResults.sectionNumber}
            </Text>
            <Text style={styles.coverPageText}>
              {t.feasibilityResults.coverPageTitle}
            </Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* F.S - Temel Gostergeler - 1 */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            {' '}
            - {t.feasibilityResults.keyIndicators}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.ratioSimExplainsText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 10,
            }}
          >
            {metricsTableData && (
              <Image
                src={metricsTableData}
                style={{
                  width: '100%',
                  height: 300,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S - Temel Gostergeler - 2 */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.feasibilityResults.keyIndicators}
          </Text>
        </View>
        <View style={styles.section}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
            }}
          >
            {metricsData && (
              <Image
                src={metricsData}
                style={{
                  width: '100%',
                  height: 175,
                  objectFit: 'contain',
                  alignSelf: 'center',
                  marginBottom: 20,
                }}
              />
            )}
          </View>
          <Text style={{ fontSize: 12, marginBottom: 10 }}>
            <Text style={{ fontWeight: 'bold' }}>
              {t.feasibilityResults.averageSalesPrice}
            </Text>{' '}
            {t.feasibilityResults.averageSalesPriceText}
            {'\n\n'}
            <Text style={{ fontWeight: 'bold' }}>
              {t.feasibilityResults.soldEnergy}
            </Text>{' '}
            {t.feasibilityResults.soldEnergyText}
            {'\n\n'}
            <Text style={{ fontWeight: 'bold' }}>
              {t.feasibilityResults.batteryUsageCost}
            </Text>{' '}
            {t.feasibilityResults.batteryUsageCostText}
          </Text>
          <Text style={{ fontSize: 12, marginBottom: 0 }}>
            {t.feasibilityResults.conclusionText.replace(/\\n/g, '\n')}
          </Text>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*2 - Enerji Analizi - Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.feasibilityResults.sectionNumber32}
            </Text>
            <Text style={styles.coverPageText}>
              {t.feasibilityResults.title} -{' '}
              {t.tableOfContents.sections.energyAnalysis}
            </Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* F.S*2 - Annual Energy Flow Analysis */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
            {t.feasibilityResults.energyFlowAnalysis}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.energyFlowAnalysisText}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 10,
              width: '100%',
            }}
          >
            {energyFlowAnalysisData && (
              <Image
                src={energyFlowAnalysisData}
                style={{
                  width: '100%',
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
            <Text
              style={{
                fontSize: 12,
                fontStyle: 'italic',
                color: theme.colors.grayDark,
                textAlign: 'center',
                marginTop: 10,
              }}
            >
              {t.feasibilityResults.energyFlowAnalysisTooltip}
            </Text>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*2 - SSMW / SSMWh için Enerji Analizi Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
            {t.feasibilityResults.energyAnalysis}
          </Text>
        </View>
        <View style={styles.section}>
          <View style={{ flexDirection: 'row', gap: 20 }}>
            <View
              style={{
                borderWidth: 1,
                borderColor: theme.colors.grayLightAccent,
                flex: 1,
                overflow: 'hidden',
              }}
            >
              <View style={{ flexDirection: 'row' }}>
                <View
                  style={{
                    padding: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    backgroundColor: theme.colors.primary,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: theme.colors.light,
                      textAlign: 'center',
                    }}
                  >
                    {t.feasibilityResults.storage}
                  </Text>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.grayLightAccent,
                }}
              >
                <View
                  style={{
                    padding: 1,
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme.colors.grayLightAccent,
                    flex: 1.5,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: theme.colors.dark,
                      textAlign: 'right',
                    }}
                  >
                    {t.feasibilityResults.chargedEnergy}
                  </Text>
                </View>
                <View
                  style={{
                    padding: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                >
                  <Text style={{ fontSize: 10, textAlign: 'center' }}>
                    {formatNumberWithCommas(
                      dataFlowTotal?.graphData[0].chargedFromGeneration +
                        dataFlowTotal?.graphData[0].chargedFromDayAhead
                    )}{' '}
                    MWh
                  </Text>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.grayLightAccent,
                }}
              >
                <View
                  style={{
                    padding: 1,
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme.colors.grayLightAccent,
                    flex: 1.5,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: theme.colors.dark,
                      textAlign: 'right',
                    }}
                  >
                    {t.feasibilityResults.energyLoss}
                  </Text>
                </View>
                <View
                  style={{
                    padding: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                >
                  <Text style={{ fontSize: 10, textAlign: 'center' }}>
                    {formatNumberWithCommas(
                      dataFlowTotal?.graphData[0].chargedFromGeneration +
                        dataFlowTotal?.graphData[0].chargedFromDayAhead -
                        dataFlowTotal?.graphData[0].discharged
                    )}{' '}
                    MWh
                  </Text>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.grayLightAccent,
                }}
              >
                <View
                  style={{
                    padding: 1,
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme.colors.grayLightAccent,
                    flex: 1.5,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: theme.colors.dark,
                      textAlign: 'right',
                    }}
                  >
                    {t.feasibilityResults.netDischarge}
                  </Text>
                </View>
                <View
                  style={{
                    padding: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                >
                  <Text style={{ fontSize: 10, textAlign: 'center' }}>
                    {formatNumberWithCommas(
                      dataFlowTotal?.graphData[0].discharged
                    )}{' '}
                    MWh
                  </Text>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.grayLightAccent,
                }}
              >
                <View
                  style={{
                    padding: 1,
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme.colors.grayLightAccent,
                    flex: 1.5,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: theme.colors.dark,
                      textAlign: 'right',
                    }}
                  >
                    {t.feasibilityResults.cycleCount}
                  </Text>
                </View>
                <View
                  style={{
                    padding: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                >
                  <Text style={{ fontSize: 10, textAlign: 'center' }}>
                    {formatNumberWithCommas(
                      Number(bestSimulation?.result.cycleNumber.toFixed(0))
                    )}{' '}
                    {t.feasibilityResults.cycle}
                  </Text>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                }}
              >
                <View
                  style={{
                    padding: 1,
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme.colors.grayLightAccent,
                    flex: 1.5,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: theme.colors.dark,
                      textAlign: 'right',
                    }}
                  >
                    {t.feasibilityResults.batteryReplacementYear}
                  </Text>
                </View>
                <View
                  style={{
                    padding: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                >
                  <Text style={{ fontSize: 10, textAlign: 'center' }}>
                    {replacementYears && replacementYears.length > 0
                      ? replacementYears.join(' & ')
                      : '-'}
                  </Text>
                </View>
              </View>
            </View>
            <Text style={{ fontSize: 12, color: theme.colors.dark, flex: 2 }}>
              {t.feasibilityResults.studyResultText.includes(
                '{finalBatteryValue}'
              ) ? (
                <>
                  {t.feasibilityResults.studyResultText
                    .split('{finalBatteryValue}')[0]
                    .replace(/\\n/g, '\n')}
                  <Text style={styles.blueText}>
                    %{(batteryDegData.endSoh * 100).toFixed(2) || '0'}
                  </Text>
                  {t.feasibilityResults.studyResultText
                    .split('{finalBatteryValue}')[1]
                    .split('{efficiency}')[0]
                    .replace(/\\n/g, '\n')}
                  <Text style={styles.blueText}>
                    %{storageData.netOverallRoundTripEfficiency.toFixed(2)}
                  </Text>
                  {t.feasibilityResults.studyResultText
                    .split('{efficiency}')[1]
                    .split('{energyLoss}')[0]
                    .replace(/\\n/g, '\n')}
                  <Text style={styles.blueText}>
                    {formatNumberWithCommas(
                      dataFlowTotal?.graphData[0].chargedFromGeneration +
                        dataFlowTotal?.graphData[0].chargedFromDayAhead -
                        dataFlowTotal?.graphData[0].discharged
                    )}{' '}
                    MWh
                  </Text>
                  {t.feasibilityResults.studyResultText
                    .split('{energyLoss}')[1]
                    .split('{cycleCount}')[0]
                    .replace(/\\n/g, '\n')}
                  <Text style={styles.blueText}>
                    {formatNumberWithCommas(
                      Number(bestSimulation?.result.cycleNumber.toFixed(0))
                    )}
                  </Text>
                  {t.feasibilityResults.studyResultText
                    .split('{cycleCount}')[1]
                    .replace(/\\n/g, '\n')}
                </>
              ) : (
                t.feasibilityResults.studyResultText.replace(/\\n/g, '\n')
              )}
            </Text>
          </View>
          <View
            style={{
              flexDirection: 'row',
              gap: 20,
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                padding: 0,
                width: '100%',
              }}
            >
              {batteryChargeData && (
                <Image
                  src={batteryChargeData}
                  style={{
                    width: '100%',
                    height: 215,
                    objectFit: 'contain',
                    alignSelf: 'center',
                  }}
                />
              )}
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                padding: 0,
                width: '100%',
              }}
            >
              {batteryDischargeData && (
                <Image
                  src={batteryDischargeData}
                  style={{
                    width: '100%',
                    height: 215,
                    objectFit: 'contain',
                    alignSelf: 'center',
                  }}
                />
              )}
            </View>
          </View>
          <View
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontStyle: 'italic',
                textAlign: 'center',
              }}
            >
              {t.feasibilityResults.chargeDischargeNote}
            </Text>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*2 - Yan Hizmetler Katılım Analizi */}
      {isDependOnModelPfc && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>
                {t.feasibilityResults.title}{' '}
              </Text>
              <Text style={styles.headerSubtitle}>
                - {t.feasibilityResults.ancillaryServicesAnalysis}{' '}
                {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh
              </Text>
            </View>

            <View style={styles.section}>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'absolute',
                  top: 85,
                  left: 0,
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginBottom: 20,
                }}
              >
                {ancillaryTableData && (
                  <Image
                    src={ancillaryTableData}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
              <Text style={styles.text}>
                {t.feasibilityResults.ancillaryServicesText.replace(
                  /\\n/g,
                  '\n'
                )}
              </Text>
              <Text style={{ ...styles.text, marginTop: 10 }}>
                {t.feasibilityResults.ancillaryServicesDesc}
              </Text>
            </View>
            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}
      {/* F.S*2 - Enerji Yönelim Analizi SSMW / SSMWh */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.feasibilityResults.energyOrientationAnalysis}{' '}
            {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={[styles.text, { marginBottom: 5 }]}>
            {t.feasibilityResults.energyFlowChartDescription}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 5,
              marginBottom: 5,
            }}
          >
            <Text style={[styles.text, { flex: 1, minWidth: '45%' }]}>
              {t.feasibilityResults.energyFlowChartGridActivity}
            </Text>
            <Text style={[styles.text, { flex: 1, minWidth: '45%' }]}>
              {t.feasibilityResults.energyFlowChartBatteryActivity}
            </Text>
            <Text style={[styles.text, { flex: 1, minWidth: '45%' }]}>
              {t.feasibilityResults.energyFlowChartGeneration}
            </Text>
            <Text style={[styles.text, { flex: 1, minWidth: '45%' }]}>
              {t.feasibilityResults.energyFlowChartConsumption}
            </Text>
          </View>
          <Text style={styles.text}>
            {t.feasibilityResults.energyFlowChartConstraint}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '10px 0',
              padding: 0,
              width: '100%',
            }}
          >
            {energyActivityData && (
              <Image
                src={energyActivityData}
                style={{
                  width: '100%',
                  height: 225,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
          <Text style={styles.text}>
            {t.feasibilityResults.energyFlowChartNote}
          </Text>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*2 - Piyasalara Katılım Analizi SSMW / SSMWh */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.feasibilityResults.marketParticipationAnalysis}{' '}
            {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh
          </Text>
        </View>
        <View style={styles.section}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              marginBottom: 20,
              padding: 0,
              width: '100%',
            }}
          >
            {energyActivityAtMarketData && (
              <Image
                src={energyActivityAtMarketData}
                style={{
                  width: '100%',
                  height: 250,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
          <Text style={styles.text}>
            {t.feasibilityResults.marketParticipationText.replace(/\\n/g, '\n')}
          </Text>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*2 - Batarya Degredasyon ve SoH Analizi SSMW / SSMWh */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.feasibilityResults.batteryDegradationAnalysis}{' '}
            {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.batteryDegradationText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 10,
              justifyContent: 'center',
              margin: 0,
              marginTop: 20,
              padding: 0,
              width: '100%',
            }}
          >
            {batteryLifeAndCycleData && (
              <Image
                src={batteryLifeAndCycleData}
                style={{
                  width: '100%',
                  flex: 4,
                  height: 200,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
            {infoCardData && (
              <Image
                src={infoCardData}
                style={{
                  width: '100%',
                  height: 200,
                  flex: 1,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - Fınansal Analizi - Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.feasibilityResults.sectionNumber33}
            </Text>
            <Text style={styles.coverPageText}>
              {t.feasibilityResults.financialAnalysisCoverTitle}
            </Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* F.S*3 - {selectedMetric} Karsılastırma Grafik Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {formatMetricName(selectedMetric, t)}{' '}
            {t.feasibilityResults.comparisonChart}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.feasibilityAnalysisText
              .split('{simulationNumber}')
              .map((part: string, index: number) => {
                if (index === 0)
                  return <Text key={`part-${index}`}>{part}</Text>;
                const simulationNamesText = getSimulationNamesList(
                  selectedSimulations,
                  t.common.and
                );
                const restText = part
                  .replace('{simulationNames}', simulationNamesText)
                  .replace(
                    '{selectedMetric}',
                    formatMetricName(selectedMetric, t)
                  )
                  .replace(
                    '{bestSimulationName}',
                    `${fisibilityModel} (${truncateName(bestSimulation.name)})`
                  )
                  .replace(/\\n/g, '\n');

                // selectedMetric, bestSimulationName ve simulationNames için split
                const parts = restText.split(
                  new RegExp(
                    `(${formatMetricName(selectedMetric, t).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${fisibilityModel} \\(${truncateName(bestSimulation.name)}\\)|${simulationNamesText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                    'g'
                  )
                );

                return (
                  <Text key={`fragment-${index}`}>
                    <Text style={styles.blueText}>{simulationNumber}</Text>
                    {parts.map((textPart: string, partIndex: number) => {
                      if (textPart === formatMetricName(selectedMetric, t)) {
                        return (
                          <Text
                            key={`metric-${index}-${partIndex}`}
                            style={styles.blueText}
                          >
                            {textPart}
                          </Text>
                        );
                      } else if (
                        textPart ===
                        `${fisibilityModel} (${truncateName(bestSimulation.name)})`
                      ) {
                        return (
                          <Text
                            key={`best-${index}-${partIndex}`}
                            style={styles.blueText}
                          >
                            {textPart}
                          </Text>
                        );
                      } else if (textPart === simulationNamesText) {
                        return (
                          <Text
                            key={`simulations-${index}-${partIndex}`}
                            style={styles.blueText}
                          >
                            {textPart}
                          </Text>
                        );
                      } else {
                        return (
                          <Text key={`text-${index}-${partIndex}`}>
                            {textPart}
                          </Text>
                        );
                      }
                    })}
                  </Text>
                );
              })}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 10,
              width: '100%',
            }}
          >
            {chartImageData && (
              <Image
                src={chartImageData}
                style={{
                  width: '100%',
                  height:
                    selectedSimulations.length > 10
                      ? 230
                      : selectedSimulations.length > 5
                        ? 255
                        : selectedSimulations.length > 1
                          ? 275
                          : 285,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
            <Text
              style={{
                fontSize: 12,
                fontStyle: 'italic',
                color: theme.colors.grayDark,
                textAlign: 'center',
              }}
            >
              {t.feasibilityResults.chartComparisonText.replace(
                '{selectedMetric}',
                formatMetricName(selectedMetric, t)
              )}
            </Text>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - Gelir Dağılımı Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {t.feasibilityResults.incomeDistribution}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.incomeDistributionDetailedText.replace(
              /\\n/g,
              '\n'
            )}
            {'\n\n'}
            {t.feasibilityResults.energySalesText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(bestSimulation.result.grossProfitFromSaleWos.toFixed(0))
              )}
            </Text>
            {t.feasibilityResults.withStorageText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(
                  bestSimulation.result.grossProfitOfStorageFromSale.toFixed(0)
                )
              )}
            </Text>
            {t.feasibilityResults.totalText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(
                  bestSimulation.result.grossProfitFromSale?.toFixed(0) || 0
                )
              )}
            </Text>{' '}
            {t.feasibilityResults.profitAchievedText}
            {'\n\n'}
            {t.feasibilityResults.ancillaryServicesPreStorageText}{' '}
            <Text style={styles.blueText}>{currencySymbol} 0</Text>
            {t.feasibilityResults.withStorageText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(
                  bestSimulation.result.revenueFromOfferedCapacity.toFixed(0)
                )
              )}
            </Text>
            {t.feasibilityResults.totalText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(
                  bestSimulation.result.revenueFromOfferedCapacity.toFixed(0)
                )
              )}
            </Text>{' '}
            {t.feasibilityResults.profitAchievedText}
            {'\n\n'}
            {t.feasibilityResults.electricityBillReductionText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(bestSimulation.result.costSavingsFromPlant.toFixed(0))
              )}
            </Text>
            {t.feasibilityResults.withStorageText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(bestSimulation.result.costSavingsFromStorage.toFixed(0))
              )}
            </Text>
            {t.feasibilityResults.afterStorageText}{' '}
            <Text style={styles.blueText}>
              {currencySymbol}
              {formatNumberWithCommas(
                Number(
                  bestSimulation.result.costSavingsFromPlantAndStorage.toFixed(
                    0
                  )
                )
              )}
            </Text>{' '}
            {t.feasibilityResults.calculatedAsText}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 20,
            }}
          >
            {incomeBreakdownData && (
              <Image
                src={incomeBreakdownData}
                style={{
                  width: '100%',
                  height: 200,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - Depolama Primi Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh
            için {t.feasibilityResults.storagePremium}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            <Text style={styles.textBold}>
              {t.feasibilityResults.storagePremiumText}{' '}
              {t.feasibilityResults.storagePremiumDescription}
            </Text>
            {'\n\n'}
            {t.feasibilityResults.ratioSimOutputsText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 10,
              marginBottom: 10,
            }}
          >
            {analysisTableData && (
              <Image
                src={analysisTableData}
                style={{
                  width: '100%',
                  height: 170,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
          <Text style={styles.textItalic}>
            1- {t.feasibilityResults.storagePremiumNotes.note1}
            {'\n'}
            2- {t.feasibilityResults.storagePremiumNotes.note2}
            {'\n'}
            3- {t.feasibilityResults.storagePremiumNotes.note3}
            {'\n'}
            4- {t.feasibilityResults.storagePremiumNotes.note4}
            {'\n'}
            5- {t.feasibilityResults.storagePremiumNotes.note5}
            {'\n'}
            6- {t.feasibilityResults.storagePremiumNotes.note6}
            {'\n'}
          </Text>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - EBITDA - Batarya & Santral Sayfası */}
      {hasValidPlant && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>
                {t.feasibilityResults.title}{' '}
              </Text>
              <Text style={styles.headerSubtitle}>
                - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
                {t.feasibilityResults.ebitdaBatteryPlantFor}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.text}>
                <Text style={styles.blueText}>{simulationYear}</Text>{' '}
                {t.feasibilityResults.yearlyPeriodText}{' '}
                {t.feasibilityResults.totalEbitdaText}
                <Text style={styles.blueText}> EBITDA </Text>
                {t.feasibilityResults.ebitdaIsText}{' '}
                <Text style={styles.blueText}>
                  {formatCompactNumber(bestSimulation.result.ebitda)}
                </Text>{' '}
                {t.feasibilityResults.achievedAsText}
                {'\n\n'}
              </Text>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                {ebitdaWithStorageData && (
                  <Image
                    src={ebitdaWithStorageData}
                    style={{
                      width: '100%',
                      height: 240,
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
            </View>
            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}
      {/* F.S*3 - EBITDA - Batarya */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
            {t.feasibilityResults.ebitdaBatteryFor}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            <Text style={styles.blueText}>{simulationYear}</Text>{' '}
            {t.feasibilityResults.yearlyPeriodText}{' '}
            {t.feasibilityResults.totalEbitdaText}
            <Text style={styles.blueText}> EBITDA </Text>
            {t.feasibilityResults.ebitdaIsText}{' '}
            <Text style={styles.blueText}>
              {formatCompactNumber(bestSimulation.result.ebitdaStorage)}
            </Text>{' '}
            {t.feasibilityResults.achievedAsText}
            {'\n\n'}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 10,
              marginBottom: 10,
            }}
          >
            {ebitdaStorageData && (
              <Image
                src={ebitdaStorageData}
                style={{
                  width: '100%',
                  height: 240,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - Geri Odeme Suresi Batarya-Santral */}
      {hasValidPlant && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>
                {t.feasibilityResults.title}{' '}
              </Text>
              <Text style={styles.headerSubtitle}>
                - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
                {t.feasibilityResults.paybackPeriodFor}{' '}
                {t.feasibilityResults.paybackPeriodBatteryPlant}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.text}>
                {t.feasibilityResults.discountedPaybackPeriod}
                <Text style={styles.blueText}>
                  {' '}
                  {bestSimulation.result.discountedPaybackPeriodProject}{' '}
                </Text>
                {t.feasibilityResults.isYears}
                {'\n\n'}
                {t.feasibilityResults.npvCashFlowText.replace(/\\n/g, '\n')}
              </Text>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                {cashFlowWithStorageData && (
                  <Image
                    src={cashFlowWithStorageData}
                    style={{
                      width: '100%',
                      height: 240,
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
            </View>
            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}
      {/* F.S*3 - Geri Odeme Suresi Batarya */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
            {t.feasibilityResults.paybackPeriodFor}{' '}
            {t.feasibilityResults.paybackPeriodBattery}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.discountedPaybackPeriod}
            <Text style={styles.blueText}>
              <Text style={styles.blueText}>
                {' '}
                {
                  bestSimulation.result.discountedPaybackPeriodProjectStorage
                }{' '}
              </Text>
            </Text>
            {t.feasibilityResults.isYears}
            {'\n\n'}
            {t.feasibilityResults.npvCashFlowText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 10,
              marginBottom: 10,
            }}
          >
            {cashFlowStorageData && (
              <Image
                src={cashFlowStorageData}
                style={{
                  width: '100%',
                  height: 240,
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            )}
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - SSMW / SSMWh Kar ve Zarar Tablosu */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
            {t.feasibilityResults.profitLossTable}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.profitLossText.replace(/\\n/g, '\n')}
          </Text>

          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              top: 25,
              left: 0,
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 10,
            }}
          >
            <Image
              src={pnlTableData}
              style={{
                width: '95%',
                height: '100%',
                objectFit: 'contain',
                alignSelf: 'center',
              }}
            />
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* F.S*3 - SSMW / SSMWh Nakit Akışı Tablosu */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>{t.feasibilityResults.title} </Text>
          <Text style={styles.headerSubtitle}>
            - {storageData.ratedPowerMw}MW / {storageData.ratedEnergyMwh}MWh{' '}
            {t.feasibilityResults.cashFlowTable}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.feasibilityResults.cashFlowText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              top: 25,
              left: 0,
              margin: 0,
              padding: 0,
              width: '100%',
              marginTop: 10,
            }}
          >
            <Image
              src={cashFlowTableData}
              style={{
                width: '95%',
                height: '100%',
                objectFit: 'contain',
                alignSelf: 'center',
              }}
            />
          </View>
          <View
            style={{
              border: `1px solid ${theme.colors.grayLightAccent}`,
              borderRadius: 6,
              flexDirection: 'column',
              fontSize: 14,
              marginBottom: 5,
              marginTop: 220,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.feasibilityResults.irrValue}</Text>
              </View>

              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  % {formatCompactNumber(bestSimulation.result.projectIrr)}
                </Text>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>EBITDA</Text>
              </View>

              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {currencySymbol}{' '}
                  {formatCompactNumber(bestSimulation.result.ebitda)}
                </Text>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
                borderBottom: `1px solid ${theme.colors.grayLightAccent}`,
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {t.feasibilityResults.discountedPaybackPeriodHeader}
                </Text>
              </View>

              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {bestSimulation.result.discountedPaybackPeriodProject.toFixed(
                    0
                  )}{' '}
                  {Number(
                    bestSimulation.result.discountedPaybackPeriodProject || 0
                  ) > 1
                    ? t.inputAnalysis.yrs
                    : t.inputAnalysis.yr}
                </Text>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <View
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.blueLightPale,
                  borderRight: `1px solid ${theme.colors.grayLightAccent}`,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>{t.common.investment}</Text>
              </View>

              <View
                style={{
                  flex: 1,
                  height: 25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.text}>
                  {currencySymbol}{' '}
                  {formatCompactNumber(bestSimulation.result.capexWithStorage)}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Karsılastırmalı Analiz - Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.comparativeAnalysis.sectionNumber}
            </Text>
            <Text style={styles.coverPageText}>
              {t.comparativeAnalysis.title}
            </Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* Karsılastırmalı Analiz - Batarya Yatırımı Öncesi ROI Özeti */}
      {hasValidPlant && (
        <>
          <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
            <View style={styles.headerStyle}>
              <Text style={styles.headerTitle}>
                {t.comparativeAnalysis.title}{' '}
              </Text>
              <Text style={styles.headerSubtitle}>
                - {t.comparativeAnalysis.preBatteryROI}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.text}>
                {t.comparativeAnalysis.preBatteryText.replace(/\\n/g, '\n')}
              </Text>
              <View
                style={{
                  position: 'absolute',
                  top: 25,
                  left: 10,
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  marginTop: 10,
                }}
              >
                {beforeStorageTableData && (
                  <Image
                    src={beforeStorageTableData}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      alignSelf: 'center',
                    }}
                  />
                )}
              </View>
            </View>
            <Footer
              reportTitle={reportTitle}
              pageNumber={pageNumber}
              totalPages={totalPages}
              logoData={logoData}
              customization={customization}
            />
          </Page>
          {pageNumber++}
        </>
      )}
      {/* Karşılaştırmalı Analiz - Batarya Yatırımı Sonrası ROI Sayfaları */}
      {tableImages.map((tableImage, index) => (
        <Page
          key={`roi-${index}`}
          size={{ width: 842, height: 473.5 }}
          style={styles.pageStyle}
        >
          <View style={styles.headerStyle}>
            <Text style={styles.headerTitle}>
              {t.comparativeAnalysis.title}{' '}
            </Text>
            <Text style={styles.headerSubtitle}>
              - {t.comparativeAnalysis.postBatteryROI} /{' '}
              {t.comparativeAnalysis.table}-{index + 1}
            </Text>
          </View>
          <View style={styles.section}>
            <Text style={styles.text}>
              {t.comparativeAnalysis.postBatteryText.replace(/\\n/g, '\n')}
            </Text>
            <View
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 35,
                left: 10,
                margin: 0,
                padding: 0,
                width: '100%',
                marginTop: 10,
              }}
            >
              <Image
                src={tableImage}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            </View>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber + index}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </Page>
      ))}
      {(pageNumber += tableImages.length)}
      {/* Ek - Kapak Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image src={SectionsBg} style={styles.coverPageImage} />
          <View style={styles.absoluteCenterLeft}>
            <Text style={styles.coverPageTitle}>
              {t.appendices.sectionNumber}
            </Text>
            <Text style={styles.coverPageText}>{t.appendices.title}</Text>
          </View>
          <Footer
            reportTitle={reportTitle}
            pageNumber={pageNumber}
            totalPages={totalPages}
            logoData={logoData}
            customization={customization}
          />
        </View>
      </Page>
      {pageNumber++}
      {/* Ek*1 - Arayuz Linki */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>
            {t.appendices.appendixNumber1}{' '}
          </Text>
          <Text style={styles.headerSubtitle}>
            - {t.appendices.interfaceLink}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>{t.appendices.interfaceLinkTextAfter}</Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              top: 50,
              left: 0,
              margin: 0,
              padding: 0,
              width: '100%',
            }}
          >
            <Image
              src={parametersData}
              style={{
                width: '95%',
                height: '100%',
                objectFit: 'contain',
                alignSelf: 'center',
              }}
            />
          </View>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              top: 130,
              left: 0,
              margin: 0,
              padding: 0,
              width: '100%',
            }}
          >
            <Image
              src={currentLanguage === 'en' ? ExportData : ExportDataTr}
              style={{
                width: '95%',
                height: '100%',
                objectFit: 'contain',
                alignSelf: 'center',
              }}
            />
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Ek-2 - Batarya Efektif Kapasite Hesaplama Sayfası */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>
            {t.appendices.appendixNumber2}{' '}
          </Text>
          <Text style={styles.headerSubtitle}>
            - {t.appendices.batteryCapacityCalculation}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.appendices.batteryCalculationText.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              borderWidth: 1,
              borderColor: theme.colors.grayLightAccent,
              marginTop: 30,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.bluePrimary,
                  }}
                >
                  {t.appendices.effectiveCapacity}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                  }}
                >
                  {storageData?.ratedPowerMw} MWh
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                  }}
                >
                  {t.appendices.dischargeDepth}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                  }}
                >
                  {`[%${storageData?.minAllowedStateOfCharge || 0},%${storageData?.maxAllowedStateOfCharge || 0}] = %${socDiff}`}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                  }}
                >
                  {t.appendices.preDepthCapacity}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                  }}
                >
                  {`${ratedPower} / ${socDiff / 100} = ${capacityValue} MWh`}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                  }}
                >
                  {t.appendices.batteryRoundTripEfficiency}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                  }}
                >
                  {`%${roundTripEfficiency}`}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.dark,
                  }}
                >
                  {t.appendices.preEfficiencyCapacity}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                  }}
                >
                  {`${capacityValue} / ${(roundTripEfficiency / 100).toFixed(4)} ~ ${preDropCapacity} MWh`}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.grayLightAccent,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.colors.blueLightPale,
                  padding: 8,
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme.colors.grayLightAccent,
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.colors.bluePrimary,
                  }}
                >
                  {t.appendices.capexNominalCapacity}
                </Text>
              </View>
              <View
                style={{
                  padding: 8,
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  flex: 2,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: theme.colors.blueLight,
                    fontWeight: 'bold',
                  }}
                >
                  {preDropCapacity} MWh
                </Text>
              </View>
            </View>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Ek-3 - Optimum Sistem Secimi */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.pageStyle}>
        <View style={styles.headerStyle}>
          <Text style={styles.headerTitle}>
            {t.appendices.appendixNumber3}{' '}
          </Text>
          <Text style={styles.headerSubtitle}>
            - {t.appendices.optimumSystemSelection}
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.text}>
            {t.appendices.ratioSimDescription.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={[styles.boxContainer, { marginTop: 10, marginBottom: 10 }]}
          >
            <View style={styles.box}>
              <Text style={styles.boxTitle}>
                {t.appendices.gridConstraints}
              </Text>
              <Text style={{ fontSize: 12 }}>
                {t.appendices.connectionAgreement}
              </Text>
            </View>

            <View style={styles.box}>
              <Text style={styles.boxTitle}>
                {t.appendices.batteryConstraints}
              </Text>
              <Text style={{ fontSize: 12 }}>
                {t.appendices.capacityMinMax}
              </Text>
            </View>

            <View style={styles.box}>
              <Text style={styles.boxTitle}>{t.appendices.regulation}</Text>
              <Text style={{ fontSize: 12 }}>
                {t.appendices.frequencyCapacity}
              </Text>
            </View>

            <View style={styles.box}>
              <Text style={styles.boxTitle}>
                {t.appendices.electricalConstraints}
              </Text>
              <Text style={{ fontSize: 12 }}>
                {t.appendices.energySupplyDemandBalance}
              </Text>
            </View>
          </View>
          <Text style={styles.text}>
            {t.appendices.optimumSystemSelectionText.replace(/\\n/g, '\n')}{' '}
            <Text style={styles.blueText}>{bestSimulation.createdBy}</Text>{' '}
            {t.appendices.optimumSystemFor}{' '}
            <Text style={styles.blueText}>{fisibilityModel}</Text>{' '}
            {t.appendices.determinedAs.replace(/\\n/g, '\n')}
          </Text>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'flex-start',
              marginTop: 10,
              width: '100%',
            }}
          >
            <View
              style={{
                width: '50%',
                paddingRight: 10,
                justifyContent: 'center',
              }}
            >
              <Text style={styles.text}>
                {t.appendices.pfcParticipationText.replace(/\\n/g, '\n')}
              </Text>
            </View>
            <View
              style={{
                width: '50%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image
                src={OptimumSystem}
                style={{
                  width: '100%',
                  height: 150,
                  objectFit: 'contain',
                }}
              />
            </View>
          </View>
        </View>
        <Footer
          reportTitle={reportTitle}
          pageNumber={pageNumber}
          totalPages={totalPages}
          logoData={logoData}
          customization={customization}
        />
      </Page>
      {pageNumber++}
      {/* Ending */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverPageStyle}>
        <View style={styles.absoluteCenter}>
          <Image
            src={SectionsBg}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <View style={styles.absoluteCenter}>
            <Image
              src={logoSrc}
              style={{
                width: 130,
                height: 60,
                objectFit: 'contain',
              }}
            />
            <Image
              src={logoData}
              style={{
                width: 130,
                height: 60,
                objectFit: 'contain',
              }}
            />
            <View style={styles.absoluteBottom}>
              <View style={styles.infoRow}>
                <Image src={IconLocation} style={{ width: 14, height: 14 }} />
                <Text
                  style={{
                    fontSize: 14,
                    color: theme.colors.grayPrimary,
                  }}
                >
                  {customization?.address || 'ANKARA TEKMER UP'}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Image src={IconEmail} style={{ width: 14, height: 14 }} />
                <Text
                  style={{
                    fontSize: 14,
                    color: theme.colors.grayPrimary,
                  }}
                >
                  {customization?.email || 'contact@ratioenergy.co'}
                </Text>
              </View>
              <View style={styles.infoRow}>
                <Image src={IconInternet} style={{ width: 14, height: 14 }} />
                <Text
                  style={{
                    fontSize: 14,
                    color: theme.colors.grayPrimary,
                  }}
                >
                  {customization?.domain || 'www.ratiosim.com'}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SimulationReportDocument;
